/**
 * Case Detail Page
 *
 * Complete detail view for a single case record with 7 tabs
 */

import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { persistedCaseRecords } from '../../services/mockData/cases';
import { getRecordById, deleteRecord, updateRecord } from '../../services/recordStorageService';
import { applyCaseOwnerGraphFields } from '../../services/creditBlock/creditBlockCaseCreate';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { logStatusChanged, logAssignmentChanged } from '../../services/activityLogService';
import { normalizeRecordForListView } from '../../utils/listViewRecordNormalizer';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { LayoutSection } from '../../components/detail/LayoutSection';
import { usePageLayout, usePageLayoutTabs } from '../../hooks/useObjectManager';
import { useApplicationContext } from '../../contexts/ApplicationContext';
import { ActivityTab } from '../../components/detail/ActivityTab';
import { CaseModal } from '../../components/modals/CaseModal';
import { LogCallModal } from '../../components/modals/LogCallModal';
import { LogMeetingModal } from '../../components/modals/LogMeetingModal';
import { LogTaskModal } from '../../components/modals/LogTaskModal';
import { AddNoteModal } from '../../components/modals/AddNoteModal';
import { SendEmailModal } from '../../components/modals/SendEmailModal';
import { UserPlus, XCircle, CheckCircle } from 'lucide-react';
import { SearchableSelect } from '../../components/modals/SearchableSelect';
import GobodoConfig from '../../config/gobodoConfig';
import {
  searchCreditBlockAssigneeOptions,
  usersToCreditBlockAssigneeOptions,
} from '../../services/gobodo/creditBlockNewCaseGobodoSearch';
import {
  CREDIT_BLOCK_ASSIGNEE_USER_FILTERS,
  ownerSelectOptionsForAssignee,
  parseOwnerPick,
} from '../../services/creditBlock/creditBlockAssigneePickerUtils';
import { fetchUsersForUserManagement, userKeys } from '../../services/queries/userQueries';
import {
  getCaseLinkedClientByAccountLookup,
  hydrateCreditBlockCaseOwners,
} from '../../services/queries/creditBlockCaseQueries';
import { useToast } from '../../contexts/ToastContext';
import type { Case } from '../../types/case';
import type { ListViewRecord } from '../../types/listView';

interface CaseDetailPageProps {
  id: string;
  onBack: () => void;
  onNavigateToClient?: (clientId: string) => void;
  onNavigateToContact?: (contactId: string) => void;
  initialCaseRecord?: Partial<ListViewRecord> | null;
}

export const CaseDetailPage: React.FC<CaseDetailPageProps> = ({
  id,
  onBack,
  onNavigateToClient,
  onNavigateToContact,
  initialCaseRecord,
}) => {
  const { success, error: showError } = useToast();
  const { userId, userName } = useCurrentUser();
  const queryClient = useQueryClient();
  const { currentApplication } = useApplicationContext();
  const isCreditBlock = currentApplication === 'CreditBlock';
  const [reassignOpen, setReassignOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [ownerSearchRaw, setOwnerSearchRaw] = useState('');
  const debouncedOwnerSearch = useDebouncedValue(ownerSearchRaw, 250);
  const [actionBusy, setActionBusy] = useState(false);
  const useGobodo = shouldUseGobodo();
  const allTabs = usePageLayoutTabs('Case__c');
  // PE-1462: Remove Tasks and History tabs - activities consolidated into Activity Timeline
  const hiddenTabs = new Set(['tasks', 'history', 'products', 'files']);
  const tabs = allTabs.filter((t: { id: string }) => !hiddenTabs.has(t.id));
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 'overview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const { data: rawCaseData, isLoading } = useQuery({
    queryKey: ['case', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const record = await getRecordById('Case', id);
        if (!record) throw new Error('Case not found');
        const norm = normalizeRecordForListView('Case', record);
        const r = record as Record<string, any>;
        const initial = (initialCaseRecord ?? {}) as Record<string, any>;
        const baseRecord = {
          ...initial,
          ...record,
          ...norm,
          subject: norm.subject || r.subject || r.Subject__c || initial.subject || r.label || '',
          caseNumber: norm.caseNumber || r.caseNumber || r.CaseNumber__c || r.CaseNumber || String(r.id || ''),
          owner:
            norm.owner ||
            r.owner ||
            r.Owner__c ||
            r.Owner ||
            r.OwnerId__c ||
            r.owner_id ||
            initial.owner ||
            initial.ownerSfId ||
            '',
          accountName:
            norm.accountName ||
            r.accountName ||
            r.AccountName__c ||
            r.AccountName ||
            initial.accountName ||
            '',
          accountId:
            norm.accountId ||
            r.accountId ||
            r.AccountId__c ||
            r.AccountId ||
            initial.accountId ||
            '',
          subType: norm.subType || r.subType || r.Sub_Type__c || initial.subType || '',
          description: norm.description || r.description || r.Description__c || initial.description || '',
          status: norm.status || r.status || r.Status__c || initial.status || '',
          priority: norm.priority || r.priority || r.Priority__c || initial.priority || '',
          type: norm.type || r.type || r.Type__c || initial.type || '',
          origin: norm.origin || r.origin || r.Origin__c || initial.origin || '',
          createdDate: norm.createdDate || r.createdDate || r.CreatedDate__c || initial.createdDate || '',
          lastModifiedDate:
            norm.lastModifiedDate ||
            r.lastModifiedDate ||
            r.LastModified__c ||
            r.lastModified ||
            initial.lastModifiedDate ||
            '',
        };
        if (isCreditBlock) {
          const [hydrated] = await hydrateCreditBlockCaseOwners([baseRecord as ListViewRecord]);
          return hydrated ?? baseRecord;
        }
        return baseRecord;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allCases = persistedCaseRecords.get();
      const found = allCases.find(c => c.id === id);
      if (!found) throw new Error('Case not found');
      return found;
    }
  });
  const caseAccountLookup = String(rawCaseData?.accountId ?? (rawCaseData as any)?.AccountId__c ?? '').trim();
  const { data: linkedCaseClient } = useQuery({
    queryKey: ['credit-block', 'case-detail-linked-client', caseAccountLookup],
    queryFn: () => getCaseLinkedClientByAccountLookup(caseAccountLookup),
    enabled:
      isCreditBlock &&
      useGobodo &&
      !!caseAccountLookup &&
      !String(rawCaseData?.accountName ?? '').trim(),
    staleTime: 5 * 60 * 1000,
  });
  const caseData = useMemo(() => {
    if (!rawCaseData) return rawCaseData;
    const linkedAccountName = String(linkedCaseClient?.displayName ?? '').trim();
    if (linkedAccountName && !String(rawCaseData.accountName ?? '').trim()) {
      return { ...rawCaseData, accountName: linkedAccountName };
    }
    return rawCaseData;
  }, [rawCaseData, linkedCaseClient]);

  // Fetch page layout (uses Record Pages assignment system)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Case__c', caseData);

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    const displayName = caseData?.caseNumber ?? caseData?.subject ?? (caseData as any)?.Subject__c ?? id;
    if (!window.confirm(`Are you sure you want to delete case ${displayName}?`)) return;
    if (shouldUseGobodo()) {
      deleteRecord('Case__c', id)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
          queryClient.invalidateQueries({ queryKey: ['caseListViewData'] });
          queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Case__c'] });
          success('Case deleted', `Case ${displayName} has been deleted`);
          onBack();
        })
        .catch((err) => {
          console.error('[CaseDetailPage] Delete failed:', err);
          success('Error', `Failed to delete case: ${err?.message || 'Unknown error'}`);
        });
      return;
    }
    const allCases = persistedCaseRecords.get();
    persistedCaseRecords.set(allCases.filter(c => c.id !== id));
    queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
    queryClient.invalidateQueries({ queryKey: ['caseListViewData'] });
    success('Case deleted', `Case ${displayName} has been deleted`);
    onBack();
  };

  const { data: adminUsers = [], isLoading: adminUsersLoading } = useQuery({
    queryKey: userKeys.list(CREDIT_BLOCK_ASSIGNEE_USER_FILTERS),
    queryFn: () => fetchUsersForUserManagement(CREDIT_BLOCK_ASSIGNEE_USER_FILTERS),
    enabled: isCreditBlock && reassignOpen,
    staleTime: 5 * 60 * 1000,
  });

  const adminUserPickerOptions = useMemo(() => usersToCreditBlockAssigneeOptions(adminUsers), [adminUsers]);

  const { data: assigneeRemoteOptions = [], isFetching: assigneeRemoteFetching } = useQuery({
    queryKey: [
      'gobodo',
      'UsersApi',
      'searchUsers',
      'caseDetailReassign',
      GobodoConfig.graph(),
      debouncedOwnerSearch,
    ],
    queryFn: () => searchCreditBlockAssigneeOptions(debouncedOwnerSearch),
    enabled: isCreditBlock && reassignOpen && useGobodo && debouncedOwnerSearch.trim().length > 0,
    staleTime: 60_000,
  });

  const assigneePickerOptions = useMemo(() => {
    if (useGobodo) {
      if (!debouncedOwnerSearch.trim()) return adminUserPickerOptions;
      return assigneeRemoteOptions;
    }
    const q = debouncedOwnerSearch.trim().toLowerCase();
    if (!q) return adminUserPickerOptions;
    return adminUserPickerOptions.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [useGobodo, debouncedOwnerSearch, adminUserPickerOptions, assigneeRemoteOptions]);

  const ownersLoading = useGobodo
    ? debouncedOwnerSearch.trim()
      ? assigneeRemoteFetching
      : adminUsersLoading
    : adminUsersLoading;

  const ownerRows = useMemo(
    () => ownerSelectOptionsForAssignee(assigneePickerOptions, userName),
    [assigneePickerOptions, userName]
  );

  const invalidateCaseQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['case', id] });
    queryClient.invalidateQueries({ queryKey: ['credit-block', 'cases'] });
    queryClient.invalidateQueries({ queryKey: ['gobodo', 'records', 'Case__c'] });
    queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
  };

  const handleReassignCase = async () => {
    if (!newOwner.trim() || !caseData) return;
    if (useGobodo && !newOwner.startsWith('u:')) {
      showError('Reassign failed', 'Select a user from the list (with account link) so the owner relationship can be set.');
      return;
    }
    setActionBusy(true);
    const { ownerName } = parseOwnerPick(newOwner, ownerRows);
    const displayOwner = ownerName.trim() || newOwner;
    const oldOwner = String(caseData.owner || '');
    try {
      const payload: Record<string, unknown> = {};
      applyCaseOwnerGraphFields(payload, {
        ownerPickRaw: newOwner.startsWith('u:') ? newOwner : undefined,
        ownerDisplayName: displayOwner,
      });
      await updateRecord('Case__c', id, payload, { gobodoPropertyPatch: true });
      logAssignmentChanged(id, 'Case__c', oldOwner, displayOwner, userId, userName);
      invalidateCaseQueries();
      success('Case reassigned', `Now owned by ${displayOwner}`);
      setReassignOpen(false);
      setNewOwner('');
      setOwnerSearchRaw('');
    } catch (e: unknown) {
      showError('Reassign failed', e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setActionBusy(false);
    }
  };

  const handleCancelCase = async () => {
    if (!cancelReason.trim() || !caseData) return;
    setActionBusy(true);
    const oldStatus = String(caseData.status || 'New');
    try {
      await updateRecord(
        'Case__c',
        id,
        { Status__c: 'Cancelled', status: 'Cancelled', CancellationReason__c: cancelReason.trim() },
        { gobodoPropertyPatch: true }
      );
      logStatusChanged(id, 'Case__c', oldStatus, 'Cancelled', userId, userName);
      invalidateCaseQueries();
      success('Case cancelled', cancelReason.trim());
      setCancelOpen(false);
      setCancelReason('');
    } catch (e: unknown) {
      showError('Cancel failed', e instanceof Error ? e.message : 'Unknown error');
    } finally { setActionBusy(false); }
  };

  const handleCloseCase = async () => {
    if (!caseData) return;
    const oldStatus = String(caseData.status || 'New');
    try {
      await updateRecord('Case__c', id, { Status__c: 'Closed', status: 'Closed' }, { gobodoPropertyPatch: true });
      logStatusChanged(id, 'Case__c', oldStatus, 'Closed', userId, userName);
      invalidateCaseQueries();
      success('Case closed', 'Case has been closed');
    } catch (e: unknown) {
      showError('Close failed', e instanceof Error ? e.message : 'Unknown error');
    }
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Cases">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!caseData) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Cases">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Case not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  // Status badge colors
  const statusColors: Record<string, string> = {
    'New': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'In Progress': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    'Escalated': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    'On Hold': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    'Resolved': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    'Closed': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300',
    'Cancelled': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
  };

  // Priority badge colors
  const priorityColors: Record<string, string> = {
    'Low': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300',
    'Medium': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'High': 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    'Critical': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
  };

  const isOverview = ['overview', 'tab_001', 'details'].includes(activeTab);
  const isActivity = ['activity', 'tab_003'].includes(activeTab);

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Cases">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {caseData.caseNumber} - {caseData.subject}
                </h1>
              </div>
              {(caseData.accountName as string)?.trim() && (
                <p className="text-gray-600 dark:text-gray-400">
                  Client: {caseData.accountName}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[caseData.status as string] || statusColors.New}`}>
                  {caseData.status}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[caseData.priority as string] || priorityColors.Medium}`}>
                  {caseData.priority} Priority
                </span>
                {(caseData.subType as string)?.trim() && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                    {caseData.subType}
                  </span>
                )}
                {(caseData.owner as string)?.trim() && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    Owner: {caseData.owner}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2 font-mono">
                Case {caseData.caseNumber}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isCreditBlock && caseData.status !== 'Closed' && caseData.status !== 'Cancelled' && (
                <>
                  <button
                    onClick={() => {
                      setNewOwner('');
                      setOwnerSearchRaw('');
                      setReassignOpen(true);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                    <UserPlus className="h-3.5 w-3.5" />Reassign
                  </button>
                  <button onClick={handleCloseCase}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors">
                    <CheckCircle className="h-3.5 w-3.5" />Close
                  </button>
                  <button onClick={() => setCancelOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
                    <XCircle className="h-3.5 w-3.5" />Cancel
                  </button>
                </>
              )}
              <button
                onClick={handleEdit}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {isOverview && (
              <div className="space-y-6">
                {isCreditBlock ? (
                  <>
                    <CreditBlockCaseOverviewSection title="Case Information" record={caseData} fields={[
                      { key: 'caseNumber', label: 'Case Number' },
                      { key: 'subject', label: 'Subject' },
                      { key: 'subType', label: 'Sub-Type' },
                      { key: 'status', label: 'Status', badge: true },
                      { key: 'priority', label: 'Priority', badge: true },
                      { key: 'type', label: 'Type' },
                      { key: 'origin', label: 'Origin' },
                    ]} />
                    <CreditBlockCaseOverviewSection title="Assignment & Client" record={caseData} fields={[
                      { key: 'owner', label: 'Owner' },
                      { key: 'accountName', label: 'Client' },
                    ]} />
                    {(caseData.description as string)?.trim() && (
                      <CreditBlockCaseOverviewSection title="Description" record={caseData} fields={[
                        { key: 'description', label: 'Description' },
                      ]} />
                    )}
                    <CreditBlockCaseOverviewSection title="System" record={caseData} fields={[
                      { key: 'createdDate', label: 'Created' },
                      { key: 'lastModifiedDate', label: 'Last Modified' },
                    ]} />
                  </>
                ) : loadingLayout ? (
                  <div className="space-y-6">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  pageLayout?.sections.map(section => (
                    <LayoutSection key={section.id} section={section} objectApiName="Case" record={caseData} />
                  ))
                )}
              </div>
            )}

            {isActivity && (
              <ActivityTab
                recordId={caseData.id as string}
                recordType="case"
                onLogActivity={(type) => {
                  if (type === 'call') setShowCallModal(true);
                  if (type === 'meeting') setShowMeetingModal(true);
                  if (type === 'task') setShowTaskModal(true);
                  if (type === 'note') setShowNoteModal(true);
                }}
              />
            )}

            {/* Products and Files tabs hidden for Credit Block */}
          </div>
        </div>
      </div>

      {/* Case Edit Modal */}
      <CaseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        caseRecord={caseData}
      />

      {/* Log Call Modal */}
      <LogCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        recordId={caseData.id as string}
        recordType="case"
        contactName={caseData.accountName as string}
      />

      {/* Log Meeting Modal */}
      <LogMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        recordId={caseData.id as string}
        recordType="case"
      />

      {/* Log Task Modal */}
      <LogTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        recordId={caseData.id as string}
        recordType="case"
        contactName={caseData.subject as string || caseData.caseNumber as string}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        recordId={caseData.id as string}
        recordType="case"
      />

      {/* Send Email Modal */}
      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        recordId={caseData.id as string}
        recordType="case"
        recipientEmail={undefined}
        recipientName={caseData.accountName as string}
        emailContext={{ case: caseData as Case }}
      />

      {/* Reassign Case Modal */}
      {reassignOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50"
          onClick={() => {
            setReassignOpen(false);
            setOwnerSearchRaw('');
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reassign Case</h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              {(caseData.owner as string)?.trim() ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Current owner: <span className="font-medium text-gray-900 dark:text-gray-100">{String(caseData.owner)}</span>
                </p>
              ) : null}
              <SearchableSelect
                label="New owner"
                name="newOwner"
                value={newOwner}
                onChange={(val) => setNewOwner(val)}
                options={ownerRows}
                skipLocalFilter
                onSearchTermChange={setOwnerSearchRaw}
                placeholder={
                  ownersLoading && ownerRows.length === 0
                    ? 'Loading users…'
                    : useGobodo && debouncedOwnerSearch.trim() && assigneeRemoteFetching
                      ? 'Searching users…'
                      : 'Search users (same list as Admin → Users)…'
                }
                required
              />
              {useGobodo ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Choose someone from the search results so the case owner is linked to their Gobodo user (not display name only).
                </p>
              ) : null}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setReassignOpen(false);
                  setOwnerSearchRaw('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleReassignCase}
                disabled={
                  !newOwner.trim() ||
                  actionBusy ||
                  (useGobodo && !newOwner.startsWith('u:'))
                }
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                {actionBusy ? 'Reassigning...' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Case Modal */}
      {cancelOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50" onClick={() => setCancelOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Cancel Case</h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Cancellation reason <span className="text-red-500">*</span>
              </label>
              <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                placeholder="Why is this case being cancelled?" autoFocus />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end gap-3">
              <button type="button" onClick={() => setCancelOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Close</button>
              <button type="button" onClick={handleCancelCase} disabled={!cancelReason.trim() || actionBusy}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50">
                {actionBusy ? 'Cancelling...' : 'Cancel Case'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
};

const STATUS_BADGE: Record<string, string> = {
  New: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  'In Progress': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  Escalated: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  Closed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
};
const PRIORITY_BADGE: Record<string, string> = {
  High: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  Medium: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  Low: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
};

function CreditBlockCaseOverviewSection({ title, record, fields }: {
  title: string;
  record: Record<string, any>;
  fields: { key: string; label: string; badge?: boolean }[];
}) {
  return (
    <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        {fields.map((f) => {
          const val = String(record[f.key] ?? '').trim();
          return (
            <div key={f.key}>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{f.label}</dt>
              <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                {f.badge && val ? (
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    (f.key === 'status' ? STATUS_BADGE[val] : PRIORITY_BADGE[val]) || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}>{val}</span>
                ) : val || '—'}
              </dd>
            </div>
          );
        })}
      </div>
    </div>
  );
}
